// Money expected to come IN, for the Cash Plan.
//
//   1. Unpaid approvedInvoices (Draft / Approved / Sent, not covered by
//      deposits — same allocation as Deposits, utils/depositsInvoiceMatching).
//   2. Scheduled visits that have not happened yet in a date window:
//        recurring jobs  -> getOccurrencesForJob, minus completed / invoiced /
//                           excluded visits (canonical keys), priced per visit
//                           from Priority's fee fields:
//                           occurrenceOverrides[visit] amount, else
//                           recurringInvoiceAmount, else estimatedTotal;
//                           the series' first visit uses initialAmount when
//                           it has one and it hasn't been invoiced yet.
//        one-time jobs   -> not completed / invoiced, startDate in window,
//                           estimatedTotal.
//      A completed visit already has (or is about to get) an invoice, so it is
//      only counted through #1 — never both.

import moment from "moment";
import { getOccurrencesForJob } from "./getOccurrencesForJob";
import {
  isRecurring,
  toDateKey,
  makeOccurrenceDateMatcher,
  getOverrideScheduledKey,
} from "./recurringOccurrenceUtils";
import {
  allocateDepositsToInvoices,
  invoiceBalance,
  invoiceStatus,
  invoiceTotal,
  isInvoiceCovered,
  isVoidInvoice,
  numberValue,
} from "./depositsInvoiceMatching";

const CLOSED_JOB = ["cancelled", "canceled", "deleted", "deactivated", "inactive", "terminated", "expired", "closed", "on hold"];

const jobIsClosed = (job = {}) => {
  const status = String(job.status || "").trim().toLowerCase();
  return CLOSED_JOB.includes(status) || job.isActive === false || job.deleted === true;
};

const firstNumber = (...values) => {
  for (const v of values) {
    const n = numberValue(v);
    if (n > 0) return n;
  }
  return 0;
};

/** Per-visit fee for one recurring visit (canonical key). */
export const recurringVisitFee = (job = {}, canonicalKey = "") => {
  const override = job.occurrenceOverrides?.[canonicalKey] || null;
  const isFirstVisit = canonicalKey && canonicalKey === toDateKey(job.startDate);
  const initial = numberValue(job.initialAmount);
  if (isFirstVisit && initial > 0 && !job.initialAmountInvoiced && !(job.completedDates || []).length) {
    return { amount: initial, kind: "Initial fee" };
  }
  return {
    amount: firstNumber(
      override?.recurringInvoiceAmount,
      override?.estimatedTotal,
      job.recurringInvoiceAmount,
      job.monthlyAmount,
      job.estimatedTotal
    ),
    kind: `${job.recurringFeeSchedule || job.feeSchedule || "Recurring"} fee`,
  };
};

/** Visits still to come between startKey and endKey (inclusive). */
export const projectScheduledVisits = (jobs = [], startKey, endKey) => {
  if (!startKey || !endKey) return [];
  const out = [];
  // Look a little outside the window so a visit moved INTO it is found.
  const from = moment(startKey).subtract(45, "days").format("YYYY-MM-DD");
  const to = moment(endKey).add(45, "days").format("YYYY-MM-DD");

  jobs.forEach((job) => {
    if (!job || jobIsClosed(job)) return;
    const site = job.siteName || job.propertyName || "";
    if (isRecurring(job)) {
      const done = makeOccurrenceDateMatcher(job, job.completedDates);
      const invoiced = makeOccurrenceDateMatcher(job, job.invoicedDates);
      const excluded = makeOccurrenceDateMatcher(job, job.excludedDates);
      getOccurrencesForJob(job, from, to).forEach((canon) => {
        const scheduled = getOverrideScheduledKey(job, canon) || canon;
        if (scheduled < startKey || scheduled > endKey) return;
        if (done(canon) || invoiced(canon) || excluded(canon)) return;
        if (job.approvedInvoiceIdByDate?.[canon]) return;
        const fee = recurringVisitFee(job, canon);
        out.push({
          jobId: job.id,
          wo: job.wo || "",
          siteName: site,
          date: scheduled,
          occurrenceKey: canon,
          amount: fee.amount,
          kind: fee.kind,
        });
      });
      return;
    }
    const status = String(job.status || "").toLowerCase();
    if (["completed", "invoice sent", "paid"].includes(status) || job.approvedInvoiceId) return;
    const start = toDateKey(job.startDate);
    if (!start || start < startKey || start > endKey) return;
    out.push({
      jobId: job.id,
      wo: job.wo || "",
      siteName: site,
      date: start,
      occurrenceKey: start,
      amount: firstNumber(job.estimatedTotal, job.addTotal, job.total, job.proposalTotal, job.amount),
      kind: "One-time job",
    });
  });

  return out.sort((a, b) => a.date.localeCompare(b.date));
};

/** Unpaid invoices with the balance still owed on each. */
export const unpaidInvoices = (invoices = [], deposits = []) => {
  const paid = allocateDepositsToInvoices(invoices, deposits);
  return invoices
    .filter((inv) => !isVoidInvoice(inv) && invoiceTotal(inv) > 0)
    .filter((inv) => invoiceStatus(inv) !== "Paid" && inv.paid !== true)
    .filter((inv) => !isInvoiceCovered(inv, paid[inv.id] || 0))
    .map((inv) => ({ ...inv, __paid: paid[inv.id] || 0, __balance: invoiceBalance(inv, paid[inv.id] || 0) }));
};
